import { isHybridApp } from "src/lib/platform";
import TaskManagementBloc, {
  TaskKey,
  TaskResponseKnown
} from "src/state/TaskManagementBloc/TaskManagementBloc";
import {
  LabPreferenceControllerService,
  TaskResponse
} from "@9amhealth/openapi";
import { Blac, Cubit } from "blac-next";
import reportErrorSentry from "src/lib/reportErrorSentry";
import BlockingLoadingOverlayController from "../../BlockingLoadingOverlay/BlockingLoadingOverlayController";
import { taskManagementState, toast } from "src/state/state";
import { KnownProgram } from "src/state/ProgramBloc/ProgramBloc";
import { AppQueryPopupsController } from "../AppQueryPopupsBloc";
import { featureFlags } from "src/lib/featureFlags";

export default class InitialLabsBloc extends Cubit<{
  screen: "questionnaire" | "status" | "medicalInsurance" | "initialPage";
  showLearnMore: boolean;
  canSkipInitialLabsButton: boolean;
  labsVerified: boolean;
  labsRequired: boolean;
  underReview: boolean;
  showSkipInitialLabsConfirmation: boolean;
  statusTitle: "underReview" | "labsVerified" | "labsRequired";
  showLabStatusOverview: boolean;
  showPushNotificationButton?: boolean;
  questionnaireTaskFinished?: boolean;
  initialLabsTaskFinished?: boolean;
  medicalInsuranceTaskFinished?: boolean;
}> {
  questionnaireTask?: TaskResponseKnown;
  labsTask?: TaskResponseKnown;
  medicalInsuranceTask?: TaskResponseKnown;

  constructor() {
    super({
      screen: "questionnaire",
      showLearnMore: false,
      canSkipInitialLabsButton: false,
      showSkipInitialLabsConfirmation: false,
      statusTitle: "labsRequired",
      labsVerified: false,
      labsRequired: false,
      underReview: false,
      showLabStatusOverview: false
    });
    this.updateTasks();
  }

  updateTasks = () => {
    const { getTask, isTaskStatusFinal } = Blac.getBloc(TaskManagementBloc);
    this.questionnaireTask = getTask(TaskKey.INITIAL_LAB_QUESTIONNAIRE);
    this.labsTask = getTask(TaskKey.COMPLETE_INITIAL_LAB_ORDER);
    this.medicalInsuranceTask = getTask(TaskKey.MEDICAL_INSURANCE);

    this.updateState({
      questionnaireTaskFinished: isTaskStatusFinal(this.questionnaireTask),
      initialLabsTaskFinished: isTaskStatusFinal(this.labsTask),
      medicalInsuranceTaskFinished: isTaskStatusFinal(this.medicalInsuranceTask)
    });
  };

  updateState = (taskState: Partial<typeof this.state>) => {
    const stateCopy = { ...this.state, ...taskState };

    stateCopy.screen =
      this.questionnaireTask?.status === TaskResponse.status.COMPLETED
        ? "status"
        : this.medicalInsuranceTask?.status === TaskResponse.status.AVAILABLE
          ? "medicalInsurance"
          : "questionnaire";

    stateCopy.showPushNotificationButton = isHybridApp();

    stateCopy.labsVerified = Boolean(taskState.initialLabsTaskFinished);

    stateCopy.canSkipInitialLabsButton =
      this.initialLabsTaskStatusCanBeSkipped &&
      featureFlags.isLabSkippingEnabled;
    stateCopy.labsRequired = this.initialLabsTaskStatusCanBeSkipped;
    stateCopy.underReview = this.initialLabsTaskStatusUnderReview;

    stateCopy.showLabStatusOverview =
      stateCopy.labsRequired || stateCopy.labsVerified;

    if (stateCopy.labsVerified) {
      stateCopy.statusTitle = "labsVerified";
    } else if (stateCopy.labsRequired) {
      stateCopy.statusTitle = "labsRequired";
    } else if (stateCopy.underReview) {
      stateCopy.statusTitle = "underReview";
    }

    this.emit(stateCopy);
  };

  setShowLearnMore = (show: boolean) => {
    this.patch({ showLearnMore: show });
  };

  get initialLabsTaskStatusUnderReview() {
    const status = this.labsTask?.status;
    return status === TaskResponse.status.LOCKED;
  }

  get initialLabsTaskStatusCanBeSkipped() {
    const status = this.labsTask?.status;
    return (
      status === TaskResponse.status.AVAILABLE ||
      status === TaskResponse.status.IN_PROGRESS
    );
  }

  get questionnaireTaskCompleted() {
    const status = this.questionnaireTask?.status;
    return (
      status !== TaskResponse.status.AVAILABLE &&
      status !== TaskResponse.status.IN_PROGRESS
    );
  }

  skipInitialLabs = async () => {
    BlockingLoadingOverlayController.startLoading({
      bg: "transparent"
    });
    try {
      await LabPreferenceControllerService.pauseLabCadenceForOnboarding();
      // reload the tasks for the onboarding program
      await taskManagementState.loadProgramTasks(KnownProgram.ONBOARDING);
      // close the info and confirmation dialog
      this.patch({
        showSkipInitialLabsConfirmation: false,
        showLearnMore: false
      });
      // then close the popup
      AppQueryPopupsController.closePopup();
    } catch (e: unknown) {
      toast.error("error.skipInitialLabs");
      reportErrorSentry(e);
    } finally {
      BlockingLoadingOverlayController.endLoading();
    }
  };

  setShowSkipInitialLabsConfirmation = (show: boolean) => {
    this.patch({ showSkipInitialLabsConfirmation: show });
  };
}
